// src/components/timetable/modern/ModernTimetableGrid.tsx
//
// Ported from the mobile app's TimetableGrid.tsx. RN's horizontal ScrollView
// grid becomes a CSS flex grid with `overflow-x: auto`; PeriodCell/
// ClassScheduleCell/TeacherCell/SingleTeacherRow are kept as the same named
// sub-components with the same state logic (live/present/arranged/covering),
// just rendered as divs instead of RN Views.

import React from 'react';
import {
  AddArrangementPayload,
  ArrangementPeriod,
  C,
  CLASS_COL_W,
  ClassTimetable,
  DayOfWeek,
  HEADER_H,
  PERIOD_COL_W,
  ROW_H,
  ST_PERIOD_W,
  TeacherArrangement,
  TimetableItem,
  ViewMode,
  formatTime,
  isCurrentPeriod,
  isScheduledForDay,
} from './modernTimetableTypes';
import ModernArrangementsView from './ModernArrangementsView';

// ─── Dots ─────────────────────────────────────────────────────────────────────

const BlinkingDot: React.FC = () => <span className="mtg-live-dot" />;
const PresentDot:  React.FC = () => <span className="mtg-present-dot" />;

// ─── PeriodCell (fixed left column, classes/all_teachers views) ───────────────

const PeriodCell: React.FC<{
  label: string; startTime: string; endTime: string; isLast: boolean; isLive: boolean;
}> = ({ label, startTime, endTime, isLast, isLive }) => (
  <div
    className="mtg-period-cell"
    style={{
      borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
      background: isLive ? 'rgba(22,163,74,0.06)' : '#F8F9FC',
    }}
  >
    {isLive && (
      <div className="mtg-live-row"><BlinkingDot /><span className="mtg-live-text">Now</span></div>
    )}
    <div className="mtg-period-label" style={{ color: isLive ? C.live : C.primary }}>{label}</div>
    <div className="mtg-period-time">{formatTime(startTime)}</div>
    <div className="mtg-period-time">{formatTime(endTime)}</div>
  </div>
);

// ─── ClassScheduleCell (classes view) ──────────────────────────────────────────

const ClassScheduleCell: React.FC<{
  item: TimetableItem | undefined; isLast: boolean; isLive: boolean;
  isTeacherPresent?: boolean; arrangement?: TeacherArrangement;
}> = ({ item, isLast, isLive, isTeacherPresent, arrangement }) => {
  if (!item) return (
    <div className="mtg-class-cell mtg-cell-empty" style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}` }}>
      <span className="mtg-dash">—</span>
    </div>
  );
  const isBreak = item.slot_type === 'break';
  const accent  = isBreak ? C.break : isTeacherPresent ? C.present : C.primary;
  const bg      = isBreak ? C.breakBg : isTeacherPresent ? (isLive ? 'rgba(251,191,36,0.22)' : C.presentBg) : (isLive ? C.primaryBgDk : C.primaryBg);
  const hasArr  = !isBreak && !!arrangement;
  return (
    <div
      className="mtg-class-cell"
      style={{
        background: bg,
        borderLeft: isTeacherPresent && !isBreak ? '2px solid #F59E0B' : 'none',
        borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
      }}
    >
      <div className="mtg-strip" style={{ background: hasArr ? C.affected : accent }} />
      <div className="mtg-cell-content">
        <div className="mtg-subject" style={{ color: hasArr ? C.affected : accent }}>{item.subject_name}</div>
        {!isBreak && item.teacher_name && (
          <div className="mtg-sub-row">
            <i className="pi pi-user" style={{ fontSize: 9, color: hasArr ? C.absent : C.textLight }} />
            <span className="mtg-sub-text" style={{ color: hasArr ? C.absent : C.textLight, opacity: hasArr ? 0.85 : 1 }}>{item.teacher_name}</span>
            {isTeacherPresent && !hasArr && <PresentDot />}
          </div>
        )}
        {hasArr && (
          <div className="mtg-sub-row">
            <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} />
            <span className="mtg-sub-text" style={{ color: C.affected, fontWeight: 700 }}>{arrangement!.arranged_teacher_name}</span>
          </div>
        )}
        {item.room_number && (
          <div className="mtg-sub-row">
            <i className="pi pi-map-marker" style={{ fontSize: 9, color: C.textLight }} />
            <span className="mtg-sub-text" style={{ color: C.textLight }}>{item.room_number}</span>
          </div>
        )}
      </div>
      {isBreak && <i className="pi pi-shopping-bag mtg-corner-icon" style={{ color: C.break }} />}
      {isLive && !isBreak && <span className="mtg-live-mark" />}
      {isTeacherPresent && !isBreak && !hasArr && <span className="mtg-present-mark" />}
    </div>
  );
};

// ─── TeacherCell (all_teachers view) ───────────────────────────────────────────

const TeacherCell: React.FC<{
  item: TimetableItem | undefined; isLast: boolean; isLive: boolean;
  className?: string; isTeacherPresent?: boolean;
  coveredByArrangement?: TeacherArrangement;
  coveringArrangement?: TeacherArrangement;
}> = ({ item, isLast, isLive, className, isTeacherPresent, coveredByArrangement, coveringArrangement }) => {
  const borderBottom = isLast ? 'none' : `1px solid ${C.border}`;

  if (!item && !coveringArrangement) return (
    <div className="mtg-class-cell mtg-cell-empty" style={{ borderBottom }}>
      <span className="mtg-dash">—</span>
    </div>
  );

  if (!item && coveringArrangement) {
    return (
      <div className="mtg-class-cell" style={{ background: C.affectedBg, borderLeft: `2px solid ${C.affected}`, borderBottom }}>
        <div className="mtg-strip" style={{ background: C.affected }} />
        <div className="mtg-cell-content">
          <div className="mtg-subject" style={{ color: C.affected }}>{coveringArrangement.subject_name}</div>
          <div className="mtg-sub-row">
            <i className="pi pi-building" style={{ fontSize: 9, color: C.textLight }} />
            <span className="mtg-sub-text" style={{ color: C.textLight }}>
              {coveringArrangement.class_name}{coveringArrangement.section ? ` · ${coveringArrangement.section}` : ''}
            </span>
          </div>
          <div className="mtg-sub-row">
            <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} />
            <span className="mtg-sub-text" style={{ color: C.affected, fontStyle: 'italic' }}>covers {coveringArrangement.primary_teacher_name}</span>
          </div>
        </div>
        {isLive && <span className="mtg-live-mark" />}
      </div>
    );
  }

  const isBreak    = item!.slot_type === 'break';
  const isCovered  = !isBreak && !!coveredByArrangement;
  const isCovering = !isBreak && !!coveringArrangement;
  const isAbsent   = !isBreak && !isTeacherPresent && !!item;
  const accent = isBreak ? C.break
    : isCovered  ? C.absent
    : isCovering ? C.affected
    : isAbsent   ? C.absent
    : isTeacherPresent ? C.present
    : C.primary;
  const bg = isBreak ? C.breakBg
    : isCovered  ? C.absentBg
    : isCovering ? C.affectedBg
    : isAbsent   ? C.absentBg
    : isTeacherPresent ? (isLive ? 'rgba(251,191,36,0.22)' : C.presentBg)
    : (isLive ? C.primaryBgDk : C.primaryBg);

  return (
    <div
      className="mtg-class-cell"
      style={{
        background: bg,
        borderLeft: (isTeacherPresent || isCovering) && !isBreak ? `2px solid ${isCovering ? C.affected : '#F59E0B'}` : 'none',
        borderBottom,
      }}
    >
      <div className="mtg-strip" style={{ background: accent }} />
      <div className="mtg-cell-content">
        <div className="mtg-subject" style={{ color: accent }}>{isBreak ? 'Break' : item!.subject_name}</div>
        {!isBreak && (
          <div className="mtg-sub-row">
            <i className="pi pi-building" style={{ fontSize: 9, color: C.textLight }} />
            <span className="mtg-sub-text" style={{ color: C.textLight }}>
              {className ? `${className}${item!.section ? ' · ' + item!.section : ''}` : (item!.section ?? '—')}
            </span>
          </div>
        )}
        {isCovered && (
          <div className="mtg-sub-row">
            <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} />
            <span className="mtg-sub-text" style={{ color: C.affected, fontWeight: 700 }}>{coveredByArrangement!.arranged_teacher_name}</span>
          </div>
        )}
        {isCovering && (
          <div className="mtg-sub-row">
            <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} />
            <span className="mtg-sub-text" style={{ color: C.affected, fontStyle: 'italic' }}>covers {coveringArrangement!.primary_teacher_name}</span>
          </div>
        )}
      </div>
      {isBreak && <i className="pi pi-shopping-bag mtg-corner-icon" style={{ color: C.break }} />}
      {isLive && !isBreak && <span className="mtg-live-mark" />}
      {isTeacherPresent && !isBreak && !isCovering && <span className="mtg-present-mark" />}
    </div>
  );
};

// ─── SingleTeacherRow (single_teacher view) ────────────────────────────────────

const SingleTeacherRow: React.FC<{
  periodLabel: string; startTime: string; endTime: string;
  item: TimetableItem | undefined; isBreakPeriod: boolean;
  isLast: boolean; isLive: boolean;
  timetableData: ClassTimetable[]; isTeacherPresent: boolean;
  coveredByArrangement?: TeacherArrangement;
  coveringArrangement?: TeacherArrangement;
}> = ({ periodLabel, startTime, endTime, item, isBreakPeriod, isLast, isLive, timetableData, isTeacherPresent, coveredByArrangement, coveringArrangement }) => {

  const teacherClassName = item && item.slot_type !== 'break'
    ? (timetableData.find((c) => c.schedule.some((s) => s.id === item.id))?.class_name ?? '')
    : '';

  const periodCell = (
    <div className="mtg-st-period" style={{ background: isLive ? 'rgba(22,163,74,0.08)' : '#F8F9FC' }}>
      {isLive && <div className="mtg-live-row"><BlinkingDot /><span className="mtg-live-text">Now</span></div>}
      <div className="mtg-period-label" style={{ color: isLive ? C.live : C.primary }}>{periodLabel}</div>
      <div className="mtg-period-time">{formatTime(startTime)}</div>
      <div className="mtg-period-time">{formatTime(endTime)}</div>
    </div>
  );

  let classContent: React.ReactNode;

  if (isBreakPeriod) {
    classContent = (
      <div className="mtg-st-class" style={{ background: C.breakBg, justifyContent: 'center' }}>
        <i className="pi pi-shopping-bag" style={{ color: C.break, fontSize: 14 }} />
        <span style={{ color: C.break, fontWeight: 700, fontSize: 13, marginLeft: 6 }}>Break</span>
      </div>
    );
  } else if (item) {
    const isCovered  = !!coveredByArrangement;
    const isCovering = !!coveringArrangement;
    const accent = isCovered ? C.affected : isTeacherPresent ? C.present : C.primary;
    const bg     = isCovered ? C.affectedBg : isTeacherPresent ? (isLive ? 'rgba(251,191,36,0.22)' : C.presentBg) : (isLive ? C.primaryBgDk : C.primaryBg);
    classContent = (
      <div className="mtg-st-class" style={{ background: bg, position: 'relative' }}>
        {isTeacherPresent && !isCovering && <span className="mtg-present-bar" />}
        <div className="mtg-strip" style={{ background: accent }} />
        <div className="mtg-cell-content">
          <div className="mtg-subject" style={{ color: accent }}>{item.subject_name}</div>
          <div className="mtg-sub-row">
            <i className="pi pi-building" style={{ fontSize: 9, color: C.textLight }} />
            <span className="mtg-sub-text" style={{ color: C.textLight }}>
              {teacherClassName ? `${teacherClassName}${item.section ? ' · ' + item.section : ''}` : (item.section ?? '—')}
            </span>
          </div>
          {isCovered && (
            <div className="mtg-sub-row">
              <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} />
              <span style={{ color: C.affected, fontWeight: 700, fontSize: 10.5 }}>{coveredByArrangement!.arranged_teacher_name}</span>
            </div>
          )}
          {isCovering && (
            <div className="mtg-sub-row">
              <i className="pi pi-user" style={{ fontSize: 9, color: C.textLight }} />
              <span className="mtg-sub-text" style={{ color: C.textLight, fontStyle: 'italic' }}>covers {coveringArrangement!.primary_teacher_name}</span>
            </div>
          )}
        </div>
        {isLive && <span className="mtg-live-mark" />}
        {isTeacherPresent && !isCovering && <span className="mtg-present-mark" />}
      </div>
    );
  } else {
    const isCovering = !!coveringArrangement;
    const bg = isCovering ? C.affectedBg : isTeacherPresent ? 'rgba(251,191,36,0.07)' : C.vacantBg;
    classContent = (
      <div className="mtg-st-class" style={{ background: bg, position: 'relative', borderLeft: isCovering ? `2px solid ${C.affected}` : 'none' }}>
        {isTeacherPresent && !isCovering && <span className="mtg-present-bar" />}
        <div className="mtg-strip" style={{ background: isCovering ? C.affected : C.vacant }} />
        <div className="mtg-cell-content">
          {isCovering ? (
            <>
              <div className="mtg-subject" style={{ color: C.affected }}>{coveringArrangement!.subject_name}</div>
              <div className="mtg-sub-row">
                <i className="pi pi-building" style={{ fontSize: 9, color: C.textLight }} />
                <span className="mtg-sub-text" style={{ color: C.textLight }}>
                  {coveringArrangement!.class_name}{coveringArrangement!.section ? ` · ${coveringArrangement!.section}` : ''}
                </span>
              </div>
              <div className="mtg-sub-row">
                <i className="pi pi-user" style={{ fontSize: 9, color: C.textLight }} />
                <span className="mtg-sub-text" style={{ color: C.textLight, fontStyle: 'italic' }}>covers {coveringArrangement!.primary_teacher_name}</span>
              </div>
            </>
          ) : (
            <>
              <div className="mtg-subject" style={{ color: C.vacant }}>Vacant</div>
              <span style={{ color: C.vacant, opacity: 0.75, fontSize: 11.5, fontWeight: 500 }}>Free period</span>
            </>
          )}
        </div>
        {!isCovering && <i className="pi pi-check-circle mtg-corner-icon" style={{ color: C.vacant }} />}
        {isLive && <span className="mtg-live-mark" style={{ borderTopColor: isCovering ? C.affected : C.vacant }} />}
        {isTeacherPresent && !isCovering && <span className="mtg-present-mark" />}
      </div>
    );
  }

  return (
    <div
      className="mtg-st-row"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
        background: isLive ? 'rgba(22,163,74,0.04)' : isTeacherPresent ? 'rgba(251,191,36,0.07)' : 'transparent',
      }}
    >
      {periodCell}
      {classContent}
    </div>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface ModernTimetableGridProps {
  timetableData:    ClassTimetable[];
  allItems:         TimetableItem[];
  periodMap:        Map<string, { startTime: string; endTime: string }>;
  uniquePeriods:    string[];
  classSectionCols: { classId: string; className: string; section: string | null }[];
  allTeachers:      string[];
  presentTeachers:  Set<string>;
  selectedDay:      DayOfWeek;
  selectedTeacher:  string | null;
  viewMode:         ViewMode;
  gridLoading:      boolean;
  error:            string | null;
  fadeIn:           boolean;
  // Arrangements
  arrangementPeriods:    ArrangementPeriod[];
  arrangements:          TeacherArrangement[];
  arrangementsLoading:   boolean;
  arrangementsError:     string | null;
  addArrangement:        (payload: AddArrangementPayload) => Promise<{ success: boolean; message: string }>;
  deleteArrangement:     (id: string) => Promise<{ success: boolean; message: string }>;
  findClassItem:        (classId: string, section: string | null, period: string) => TimetableItem | undefined;
  findTeacherItem:      (teacherName: string, period: string) => TimetableItem | undefined;
  findTeacherPeriodItem:(teacherName: string, period: string) => TimetableItem | undefined;
  isBreakPeriod:        (period: string) => boolean;
  onRetry:          () => void;
}

// ─── ModernTimetableGrid ───────────────────────────────────────────────────────

const ModernTimetableGrid: React.FC<ModernTimetableGridProps> = ({
  timetableData, allItems, periodMap, uniquePeriods, classSectionCols,
  allTeachers, presentTeachers, selectedDay, selectedTeacher, viewMode,
  gridLoading, error, fadeIn,
  arrangementPeriods, arrangements, arrangementsLoading, arrangementsError,
  addArrangement, deleteArrangement,
  findClassItem, findTeacherItem, findTeacherPeriodItem, isBreakPeriod, onRetry,
}) => {

  const renderHint = () => (
    <div className="mtg-hint">
      <i className="pi pi-info-circle" style={{ fontSize: 12, color: C.textLight }} />
      {viewMode === 'classes' && <span className="mtg-hint-text">Scroll horizontally to see all classes</span>}
      {viewMode === 'all_teachers' && <span className="mtg-hint-text">Scroll horizontally to see all teachers</span>}
      {viewMode === 'single_teacher' && (
        <>
          <span className="mtg-hint-text">Period schedule for selected teacher</span>
          <div className="mtg-hint-badge"><span className="mtg-hint-dot" style={{ background: C.vacant }} /><span style={{ color: C.vacant }}>= Vacant</span></div>
        </>
      )}
      {viewMode === 'vacant_teachers' && (
        <span className="mtg-hint-text">Vacant teachers per period on {selectedDay}</span>
      )}
      {presentTeachers.size > 0 && (
        <div className="mtg-hint-badge"><span className="mtg-hint-dot" style={{ background: '#F59E0B' }} /><span style={{ color: C.present }}>= Present</span></div>
      )}
      {arrangements.length > 0 && (viewMode === 'classes' || viewMode === 'all_teachers' || viewMode === 'single_teacher') && (
        <div className="mtg-hint-badge"><i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 9, color: C.affected }} /><span style={{ color: C.affected }}>= Arranged</span></div>
      )}
    </div>
  );

  const renderLegend = () => {
    if (viewMode === 'vacant_teachers' || viewMode === 'arrangements') return null;
    const arrangedItem = arrangements.length > 0 ? [{ color: C.affected, bg: C.affectedBg, label: 'Arranged' }] : [];
    const items = viewMode === 'single_teacher'
      ? [
          { color: C.primary, bg: C.primaryBg, label: 'Teaching' },
          { color: C.vacant,  bg: C.vacantBg,  label: 'Vacant'   },
          { color: C.break,   bg: C.breakBg,   label: 'Break'    },
          { color: C.live,    bg: 'rgba(22,163,74,0.07)', label: 'Live' },
          { color: C.present, bg: C.presentBg, label: 'Present'  },
          ...arrangedItem,
        ]
      : [
          { color: C.primary, bg: C.primaryBg, label: 'Subject'  },
          { color: C.break,   bg: C.breakBg,   label: 'Break'    },
          { color: C.live,    bg: 'rgba(22,163,74,0.07)', label: 'Live' },
          { color: C.present, bg: C.presentBg, label: 'Present'  },
          ...arrangedItem,
        ];
    return (
      <div className={`mtg-legend mtg-fade ${fadeIn ? 'mtg-fade-in' : ''}`}>
        {items.map((item) => (
          <div key={item.label} className="mtg-legend-item">
            <span className="mtg-legend-dot" style={{ background: item.bg, borderColor: item.color }} />
            <span className="mtg-legend-text">{item.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderGridContent = () => {
    if (gridLoading) return (
      <div className="mtg-state-block"><i className="pi pi-spin pi-spinner" style={{ fontSize: 22, color: C.primary }} /><span className="mtg-state-text">Switching timetable...</span></div>
    );

    if (error) return (
      <div className="mtg-state-block">
        <i className="pi pi-exclamation-circle" style={{ fontSize: 30, color: '#b91c1c' }} />
        <span className="mtg-state-text" style={{ color: '#b91c1c', fontWeight: 600 }}>{error}</span>
        <button type="button" className="mtg-retry-btn" onClick={onRetry}>
          <i className="pi pi-refresh" style={{ fontSize: 12 }} /> Try Again
        </button>
      </div>
    );

    if (timetableData.length === 0) return (
      <div className="mtg-state-block">
        <i className="pi pi-calendar-times" style={{ fontSize: 32, color: C.textLight }} />
        <span className="mtg-state-text">No timetable for this term</span>
      </div>
    );

    if (viewMode === 'single_teacher' && !selectedTeacher) return (
      <div className="mtg-state-block">
        <i className="pi pi-question-circle" style={{ fontSize: 32, color: C.textLight }} />
        <span className="mtg-state-text">Select a teacher above</span>
      </div>
    );

    if (viewMode === 'single_teacher' && selectedTeacher) {
      const isPresent = presentTeachers.has(selectedTeacher);
      return (
        <div>
          {uniquePeriods.map((period, pi) => {
            const times   = periodMap.get(period)!;
            const isBreak = isBreakPeriod(period);
            const item    = isBreak ? undefined : findTeacherPeriodItem(selectedTeacher, period);
            const isLive  = isCurrentPeriod(times.startTime, times.endTime);
            const coveredByArrangement = !isBreak
              ? arrangements.find((a) =>
                  a.primary_teacher_name === selectedTeacher &&
                  item &&
                  a.subject_id === item.subject_id &&
                  (a.section ? a.section === item.section : !item.section) &&
                  a.class_id === timetableData.find((c) => c.schedule.some((s) => s.id === item.id))?.class_id
                )
              : undefined;
            const coveringArrangement = !isBreak
              ? arrangements.find((a) => {
                  if (a.arranged_teacher_name !== selectedTeacher) return false;
                  const cls = timetableData.find((c) => c.class_id === a.class_id);
                  if (!cls) return false;
                  return cls.schedule.some((s) =>
                    s.subject_id === a.subject_id &&
                    s.period_label === period &&
                    (a.section ? s.section === a.section : !s.section) &&
                    isScheduledForDay(s, selectedDay)
                  );
                })
              : undefined;
            return (
              <SingleTeacherRow
                key={period}
                periodLabel={period}
                startTime={times.startTime}
                endTime={times.endTime}
                item={item}
                isBreakPeriod={isBreak}
                isLast={pi === uniquePeriods.length - 1}
                isLive={isLive}
                timetableData={timetableData}
                isTeacherPresent={isPresent}
                coveredByArrangement={coveredByArrangement}
                coveringArrangement={coveringArrangement}
              />
            );
          })}
        </div>
      );
    }

    if (viewMode === 'vacant_teachers') {
      return (
        <div>
          <div className="mtg-vt-header">
            <div className="mtg-vt-header-period"><i className="pi pi-clock" style={{ fontSize: 13, color: C.primary }} /><span style={{ color: C.primary }}>Period</span></div>
            <div className="mtg-vt-header-vacant"><i className="pi pi-user" style={{ fontSize: 13, color: C.vacant }} /><span style={{ color: C.vacant }}>Vacant Teachers</span></div>
          </div>
          {uniquePeriods.map((period, pi) => {
            const times   = periodMap.get(period)!;
            const isLive  = isCurrentPeriod(times.startTime, times.endTime);
            const isBreak = isBreakPeriod(period);
            const isLast  = pi === uniquePeriods.length - 1;
            const vacantHere = isBreak ? [] : allTeachers.filter((teacher) =>
              !allItems.some((i) =>
                i.teacher_name === teacher &&
                i.period_label === period &&
                i.slot_type !== 'break' &&
                isScheduledForDay(i, selectedDay)
              )
            );
            return (
              <div key={period} className="mtg-vt-row" style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}`, background: isLive ? 'rgba(22,163,74,0.03)' : C.cardBg }}>
                <div className="mtg-vt-period" style={{ background: isBreak ? 'rgba(245,158,11,0.06)' : isLive ? 'rgba(22,163,74,0.06)' : '#F8F9FC' }}>
                  {isLive && <div className="mtg-live-row"><BlinkingDot /><span className="mtg-live-text">Now</span></div>}
                  <div className="mtg-period-label" style={{ color: isBreak ? C.break : isLive ? C.live : C.primary }}>{period}</div>
                  <div className="mtg-period-time">{formatTime(times.startTime)}</div>
                  <div className="mtg-period-time">{formatTime(times.endTime)}</div>
                </div>
                <div className="mtg-vt-vacant" style={{ background: isBreak ? 'rgba(245,158,11,0.04)' : isLive ? 'rgba(22,163,74,0.03)' : C.cardBg }}>
                  {isBreak ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <i className="pi pi-shopping-bag" style={{ color: C.break, fontSize: 14 }} />
                      <span style={{ color: C.break, fontWeight: 700 }}>Break</span>
                    </div>
                  ) : vacantHere.length === 0 ? (
                    <span style={{ color: C.textLight, fontStyle: 'italic', fontSize: 12.5 }}>All busy</span>
                  ) : (
                    <div className="mtg-chips-wrap">
                      {vacantHere.map((teacher) => {
                        const isPresent = presentTeachers.has(teacher);
                        return (
                          <div
                            key={teacher}
                            className="mtg-vt-chip"
                            style={{
                              background: isPresent ? 'rgba(251,191,36,0.15)' : isLive ? 'rgba(22,163,74,0.12)' : C.vacantBg,
                              borderColor: isPresent ? 'rgba(245,158,11,0.5)' : isLive ? C.live : 'rgba(16,185,129,0.3)',
                            }}
                          >
                            {isPresent && <PresentDot />}
                            <span style={{ color: isPresent ? C.present : isLive ? C.live : C.vacant, fontWeight: 700 }}>{teacher}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Classes / All Teachers horizontal scroll grid
    return (
      <div className="mtg-wrapper">
        <div className="mtg-period-col">
          <div className="mtg-col-header"><i className="pi pi-clock" style={{ fontSize: 14, color: C.primary }} /><span style={{ color: C.primary }}>Period</span></div>
          {uniquePeriods.map((period, i) => {
            const times  = periodMap.get(period)!;
            const isLive = isCurrentPeriod(times.startTime, times.endTime);
            return (
              <PeriodCell key={period} label={period} startTime={times.startTime} endTime={times.endTime} isLast={i === uniquePeriods.length - 1} isLive={isLive} />
            );
          })}
        </div>

        <div className="mtg-col-divider" />

        <div className="mtg-scroll">
          <div style={{ display: 'flex' }}>

            {viewMode === 'classes' && classSectionCols.map((col, ci) => (
              <div key={`${col.classId}-${col.section ?? ci}`} className="mtg-class-col" style={{ borderRight: ci < classSectionCols.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <div className="mtg-col-header">
                  <i className="pi pi-building" style={{ fontSize: 13, color: C.primary }} />
                  <span style={{ color: C.primary }}>{`${col.className}${col.section ? ` ${col.section}` : ''}`}</span>
                </div>
                {uniquePeriods.map((period, pi) => {
                  const times  = periodMap.get(period)!;
                  const isLive = isCurrentPeriod(times.startTime, times.endTime);
                  const item   = findClassItem(col.classId, col.section, period);
                  const isTeacherPresent = !!(item && item.teacher_name && presentTeachers.has(item.teacher_name));
                  const arrangement = item && item.slot_type !== 'break'
                    ? arrangements.find((a) =>
                        a.class_id === col.classId &&
                        (col.section ? a.section === col.section : !a.section) &&
                        a.subject_id === item.subject_id
                      )
                    : undefined;
                  return (
                    <ClassScheduleCell key={period} item={item} isLast={pi === uniquePeriods.length - 1} isLive={isLive} isTeacherPresent={isTeacherPresent} arrangement={arrangement} />
                  );
                })}
              </div>
            ))}

            {viewMode === 'all_teachers' && allTeachers.map((teacher, ti) => {
              const isColPresent = presentTeachers.has(teacher);
              return (
                <div key={teacher} className="mtg-class-col" style={{ borderRight: ti < allTeachers.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <div className="mtg-col-header" style={{ background: isColPresent ? C.presentHeader : '#F0F4FF', borderBottomColor: isColPresent ? C.presentBorder : C.borderDk }}>
                    {isColPresent ? <PresentDot /> : <i className="pi pi-user" style={{ fontSize: 13, color: C.primary }} />}
                    <span style={{ color: isColPresent ? C.present : C.primary }}>{teacher}</span>
                  </div>
                  {uniquePeriods.map((period, pi) => {
                    const times  = periodMap.get(period)!;
                    const isLive = isCurrentPeriod(times.startTime, times.endTime);
                    const item   = isBreakPeriod(period)
                      ? allItems.find((i) => i.period_label === period && i.slot_type === 'break')
                      : findTeacherItem(teacher, period);
                    const teacherClassName = item && item.slot_type !== 'break'
                      ? (timetableData.find((c) => c.schedule.some((s) => s.id === item.id))?.class_name ?? '')
                      : '';
                    const coveredByArrangement = !isBreakPeriod(period)
                      ? arrangements.find((a) =>
                          a.primary_teacher_name === teacher &&
                          item &&
                          a.subject_id === item.subject_id &&
                          (a.section ? a.section === item.section : !item.section) &&
                          a.class_id === timetableData.find((c) => c.schedule.some((s) => s.id === item.id))?.class_id
                        )
                      : undefined;
                    const coveringArrangement = !isBreakPeriod(period)
                      ? arrangements.find((a) => {
                          if (a.arranged_teacher_name !== teacher) return false;
                          const cls = timetableData.find((c) => c.class_id === a.class_id);
                          if (!cls) return false;
                          return cls.schedule.some((s) =>
                            s.subject_id === a.subject_id &&
                            s.period_label === period &&
                            (a.section ? s.section === a.section : !s.section) &&
                            isScheduledForDay(s, selectedDay)
                          );
                        })
                      : undefined;
                    return (
                      <TeacherCell
                        key={period}
                        item={item}
                        isLast={pi === uniquePeriods.length - 1}
                        isLive={isLive}
                        className={teacherClassName}
                        isTeacherPresent={isColPresent}
                        coveredByArrangement={coveredByArrangement}
                        coveringArrangement={coveringArrangement}
                      />
                    );
                  })}
                </div>
              );
            })}

          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .mtg-fade { opacity: 0; transform: translateY(14px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .mtg-fade.mtg-fade-in { opacity: 1; transform: translateY(0); }

        .mtg-card { background: ${C.cardBg}; border-radius: 16px; border: 1px solid ${C.border}; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
        .mtg-hint { display: flex; align-items: center; gap: 6px; padding: 10px 14px; border-bottom: 1px solid ${C.border}; background: #FAFBFC; font-size: 12px; flex-wrap: wrap; }
        .mtg-hint-text { color: ${C.textLight}; flex: 1; }
        .mtg-hint-badge { display: flex; align-items: center; gap: 4px; margin-left: 4px; font-weight: 600; }
        .mtg-hint-dot { width: 7px; height: 7px; border-radius: 3.5px; }

        .mtg-wrapper { display: flex; }
        .mtg-period-col { width: ${PERIOD_COL_W}px; flex-shrink: 0; background: #F8F9FC; }
        .mtg-col-divider { width: 1px; background: ${C.border}; }
        .mtg-col-header {
          height: ${HEADER_H}px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
          background: #F0F4FF; border-bottom: 1px solid ${C.borderDk}; padding: 4px 6px; font-size: 11.5px; font-weight: 700; text-align: center;
        }
        .mtg-period-cell { height: ${ROW_H}px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4px 6px; }
        .mtg-period-label { font-size: 12.5px; font-weight: 700; text-align: center; }
        .mtg-period-time { font-size: 10.5px; color: ${C.textLight}; margin-top: 1px; }

        .mtg-scroll { flex: 1; overflow-x: auto; }
        .mtg-class-col { width: ${CLASS_COL_W}px; flex-shrink: 0; }
        .mtg-class-cell { height: ${ROW_H}px; display: flex; align-items: center; position: relative; background: ${C.cardBg}; overflow: hidden; }
        .mtg-cell-empty { background: #F5F6FA; justify-content: center; }
        .mtg-dash { color: #C0C5D8; font-size: 13px; }
        .mtg-strip { width: 3px; height: 55%; border-radius: 2px; margin: 0 6px 0 8px; flex-shrink: 0; }
        .mtg-cell-content { flex: 1; min-width: 0; padding-right: 16px; }
        .mtg-subject { font-size: 12.5px; font-weight: 700; margin-bottom: 3px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .mtg-sub-row { display: flex; align-items: center; gap: 3px; margin-top: 1px; }
        .mtg-sub-text { font-size: 11px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mtg-corner-icon { position: absolute; top: 8px; right: 8px; font-size: 13px; }
        .mtg-live-mark { position: absolute; top: 0; right: 0; width: 0; height: 0; border-top: 10px solid ${C.live}; border-left: 10px solid transparent; }
        .mtg-present-mark { position: absolute; bottom: 0; right: 0; width: 0; height: 0; border-bottom: 10px solid #F59E0B; border-right: 10px solid transparent; }
        .mtg-present-bar { position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: #F59E0B; }

        .mtg-st-row { display: flex; }
        .mtg-st-period { width: ${ST_PERIOD_W}px; flex-shrink: 0; height: ${ROW_H}px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4px 8px; border-right: 1px solid ${C.border}; }
        .mtg-st-class { flex: 1; height: ${ROW_H}px; display: flex; align-items: center; position: relative; overflow: hidden; }

        .mtg-live-row { display: flex; align-items: center; gap: 3px; margin-bottom: 2px; }
        .mtg-live-dot, .mtg-present-dot { width: 7px; height: 7px; border-radius: 3.5px; display: inline-block; }
        .mtg-live-dot { background: ${C.live}; animation: mtg-blink-live 1.2s ease-in-out infinite; }
        .mtg-present-dot { background: #F59E0B; margin-right: 3px; animation: mtg-blink-present 1.6s ease-in-out infinite; }
        @keyframes mtg-blink-live { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }
        @keyframes mtg-blink-present { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
        .mtg-live-text { color: ${C.live}; font-size: 9px; font-weight: 700; }

        .mtg-state-block { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 44px 24px; text-align: center; }
        .mtg-state-text { color: ${C.textSecondary}; font-size: 13.5px; font-weight: 600; }
        .mtg-retry-btn {
          display: flex; align-items: center; gap: 6px; border: none; cursor: pointer; border-radius: 12px;
          padding: 11px 22px; margin-top: 4px; color: #fff; font-size: 13px; font-weight: 700;
          background: linear-gradient(90deg, ${C.primary}, ${C.primaryMid});
        }

        .mtg-vt-header { display: flex; background: #F0F4FF; border-bottom: 1px solid ${C.border}; height: ${HEADER_H}px; }
        .mtg-vt-header-period { width: ${PERIOD_COL_W}px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; gap: 5px; border-right: 1px solid ${C.border}; font-size: 12.5px; font-weight: 700; }
        .mtg-vt-header-vacant { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; background: #F0FDF6; font-size: 12.5px; font-weight: 700; }
        .mtg-vt-row { display: flex; min-height: ${ROW_H}px; }
        .mtg-vt-period { width: ${PERIOD_COL_W}px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px 4px; border-right: 1px solid ${C.border}; }
        .mtg-vt-vacant { flex: 1; display: flex; align-items: center; padding: 10px 12px; }
        .mtg-chips-wrap { display: flex; flex-wrap: wrap; gap: 6px; }
        .mtg-vt-chip { display: flex; align-items: center; border-radius: 7px; border: 1px solid; padding: 4px 9px; font-size: 11.5px; }

        .mtg-legend { display: flex; justify-content: center; flex-wrap: wrap; gap: 16px; background: ${C.cardBg}; border-radius: 12px; border: 1px solid ${C.border}; padding: 12px 16px; }
        .mtg-legend-item { display: flex; align-items: center; gap: 7px; }
        .mtg-legend-dot { width: 12px; height: 12px; border-radius: 6px; border-width: 1.5px; border-style: solid; display: inline-block; }
        .mtg-legend-text { color: ${C.textSecondary}; font-size: 12px; font-weight: 500; }
      `}</style>

      {viewMode === 'arrangements' ? (
        <ModernArrangementsView
          periods={arrangementPeriods}
          selectedDay={selectedDay}
          fadeIn={fadeIn}
          arrangements={arrangements}
          arrangementsLoading={arrangementsLoading}
          arrangementsError={arrangementsError}
          timetableData={timetableData}
          addArrangement={addArrangement}
          deleteArrangement={deleteArrangement}
        />
      ) : (
        <div className={`mtg-card mtg-fade ${fadeIn ? 'mtg-fade-in' : ''}`}>
          {renderHint()}
          {renderGridContent()}
        </div>
      )}
      {renderLegend()}
    </>
  );
};

export default ModernTimetableGrid;
